import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GENERIC_ERROR } from '../../../helper/constants';

interface ErrorState {
	message: string | any[];
	isError: boolean;
}

interface CreateErrorPayload {
	error: any;
}

const initialState: ErrorState = {
	message: '',
	isError: false,
};

const errorSlice = createSlice({
	name: 'errorSlice',
	initialState,
	reducers: {
		/**
		 * Generates a generic error message based on the given data.
		 *
		 * This function evaluates the provided data (typically an error response) to determine
		 * the nature and severity of the error. It then sets the error message accordingly.
		 *
		 * Rules:
		 * - If the error status is less than 500, it attempts to retrieve a specific error message
		 *   from the data response.
		 * - If the error message is a string, it uses this directly.
		 * - If the error message is not directly a string, it looks for a 'title' or 'Message' or 'message' property in the data
		 *   response and uses it if found.
		 * - If no 'title', 'message' or 'Message' is found, it attempts to extract the error from the 'errors' property of the data response.
		 * - For any other scenario or for error status 500 or above, a generic error message is shown.
		 *
		 * Assumptions:
		 * - The function assumes that the structure of the error response (`data.response.data` or `data.response.title`)
		 *   is consistent and as described.
		 *
		 */
		createError: (state, action: PayloadAction<CreateErrorPayload>) => {
			state.isError = true;
			const { error } = action.payload;
			// console.log('dispatch createError', error);
			if ((error?.status ?? 500) < 500) {
				if (typeof error?.data == 'string') {
					state.message = error.data;
				} else if ((error.data?.Message ?? error.data?.message ?? '').length > 0) {
					state.message = error.data.Message ?? error.data.message;
				} else if ((error?.data?.title ?? '').length > 0) {
					state.message = error.data.title;
				} else {
					state.message = GENERIC_ERROR;
				}
			} else {
				state.message = GENERIC_ERROR;
			}
		},
		clearError: (state) => {
			state.message = '';
			state.isError = false;
		},
	},
});

export const { createError, clearError } = errorSlice.actions;
export default errorSlice.reducer;
